import { Button, Input, Modal, Space, Typography, notification } from '@pankod/refine-antd';
import { useInput } from 'hooks/useInput';
import { useEffect, useState } from 'react';
import { getAppInstances, updateAppInstance } from 'services/app-instance';
import useAppInstanceStore from 'store/useAppInstanceStore';

interface AppInstanceEditModalProps {
  appId: string;
  isModalOpen: boolean;
  closeModal: () => void;
}

const { Text } = Typography;

export const AppInstanceEditModal: React.FC<AppInstanceEditModalProps> = ({
  appId,
  isModalOpen,
  closeModal,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { appInstances, updateAppInstance: updateAppInstanceStore } = useAppInstanceStore();
  const nameInput = useInput('');
  const descriptionInput = useInput('');

  useEffect(() => {
    const selectedAppInstance = appInstances.find((app) => app.id === appId);
    if (!selectedAppInstance) return;

    const { displayName, description } = selectedAppInstance;
    nameInput.setValue(displayName);
    descriptionInput.setValue(description);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);

  const handleSubmit = async () => {
    if (!nameInput.value.trim() || !descriptionInput.value.trim()) {
      return;
    }

    setIsLoading(true);
    try {
      const data = {
        name: nameInput.value,
        description: descriptionInput.value,
      };
      await updateAppInstance(appId, data);
      const apps = await getAppInstances();
      const updatedApp = apps.find((app) => app.id === appId)!;
      updateAppInstanceStore(appId, { ...updatedApp });
    } catch (err: any) {
      console.error(err);
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    }
    setIsLoading(false);
    closeModal();
  };

  return (
    <Modal
      title="Edit Setup"
      visible={isModalOpen}
      onCancel={closeModal}
      footer={[
        <Button key="back" onClick={closeModal}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" loading={isLoading} onClick={handleSubmit}>
          Save
        </Button>,
      ]}
    >
      <>
        <Space direction="vertical" style={{ display: 'block', marginBottom: '1rem' }}>
          <Text style={{ display: 'inline-block', marginBottom: '0.5rem', fontWeight: 600 }}>
            Name
          </Text>
          <Input
            placeholder="Identify this new setup as..."
            value={nameInput.value}
            onChange={nameInput.onChange}
          />
        </Space>

        <Space direction="vertical" style={{ display: 'block' }}>
          <Text style={{ display: 'inline-block', marginBottom: '0.5rem', fontWeight: 600 }}>
            Description
          </Text>
          <Input
            placeholder="Describe the use of this setup..."
            value={descriptionInput.value}
            onChange={descriptionInput.onChange}
          />
        </Space>
      </>
    </Modal>
  );
};
